import {
	DisabledTripTypeFlightsSearchFormInterface,
	FlightsSearchFormSubmitInterface,
} from '@smartlinks/react-design-system/dist/src/lib/flights/shared/interfaces/flights-search-form.interface';
import { FlightSearchWidgetSkeleton } from '../../templates/common/skeletons/flight-search-widget-skeleton';
import { OptionSelect } from '@smartlinks/react-design-system/dist/src/shared/interfaces/inputs.interface';
import { FlightsSearchAirlinesInterface } from '../../shared/interfaces/flights-airports.interface';
import { SearchWidgetInterface } from '../../shared/interfaces/search.interface';
import useSearchWidgetSkeleton from '../../shared/hooks/useSearchWidgetSkeleton';
import { FlightsService } from '../../shared/services/flights.service';
import { FlightsUtil } from '../../shared/utils/flights-search.util';
import useSearchWidget from '../../shared/hooks/useSearchWidget';
import { CommonsUtil } from '../../shared/utils/commons';
import styles from 'bundle-text:./flights-search.scss';
import { useEffect, useState, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';

// Carga diferida del formulario (incluye organisms del design-system + íconos FontAwesome
// vía FlightsSearchListItemsUtil): se separa en un chunk async, aligerando la carga inicial del widget.
const FlightsSearch = lazy(async () => await import('../../templates/flights-search/flights-search'));
export interface FlightsSearchWidgetProps extends SearchWidgetInterface<FlightsSearchFormSubmitInterface> {
	disabledTripType?: DisabledTripTypeFlightsSearchFormInterface;
	isNotHome?: boolean;
}

export const AppFlightsSearchWidget = ({
	token,
	language,
	agency,
	disabledTripType,
	callUrlSearch,
	callShowMobileFields,
	isNotHome,
}: FlightsSearchWidgetProps): JSX.Element => {
	const widgetClassName: string = 'flightsSearchWidget';
	const {
		agencyState,
		isLoading,
		widgetService: flightsService,
		widgetStyles,
		urlSearchHandler,
		showMobileFieldsHandler,
		t,
	} = useSearchWidget<FlightsService, FlightsSearchFormSubmitInterface>({
		className: widgetClassName,
		styles: styles?.toString() || '',
		token,
		language,
		agency,
		callUrlSearch,
		callShowMobileFields,
		productServiceClass: FlightsService,
	});

	const { showSkeleton, isFadingOut } = useSearchWidgetSkeleton(isLoading);
	const [airlines, setAirlines] = useState<OptionSelect[]>([]);
	useEffect(() => {
		if (!isLoading && !(agencyState == null) && !(Object.keys(agencyState).length === 0)) {
			((): void => {
				try {
					void (async () => {
						const tempAirlines: FlightsSearchAirlinesInterface[] | null = await flightsService.getAirlines();
						setAirlines([...FlightsUtil.mapAirlinesOptions(t, tempAirlines || [])]);
					})();

					return;
				} catch (error) {
					console.error(error);
				}
				setAirlines([]);
			})();
		}
	}, [isLoading]);

	return (
		<Suspense fallback={<div data-testid='flights-search.widget.loading-fallback'>...</div>}>
			{widgetStyles}
			<div data-testid='flights-search.widget.container' id={agencyState?.profile?.name?.replace(/\s+/g, '') || 'noName'} className={widgetClassName}>
				{showSkeleton && <FlightSearchWidgetSkeleton isFadingOut={isFadingOut} />}
				{!showSkeleton && !isLoading && (
					<FlightsSearch
						agency={agencyState}
						airlines={airlines}
						flightsService={flightsService}
						callUrlSearch={urlSearchHandler}
						t={t}
						disabledTripType={disabledTripType}
						callShowMobileFields={showMobileFieldsHandler}
						isNotHome={isNotHome}
					/>
				)}
			</div>
		</Suspense>
	);
};

export function FlightsSearchWidget(props: FlightsSearchWidgetProps): void {
	const element: HTMLDivElement | null = CommonsUtil.verifyWidgetElement(props?.elementId);
	if (element) {
		void CommonsUtil.createShadowRoot(element, {
			includeIndexStyles: true,
			includeFonts: true,
			extraCss: String.raw`
        @media(min-width: 768px) {
          .md\:h-\[571px\] { height: 571px; }
        }

        .TestVivaMx .toolTip__container,
        .VivaAerobusMX .toolTip__container {
          background: #002612 !important;
        }
      `,
		}).then((renderIn: HTMLDivElement) => {
			const root = createRoot(renderIn);
			root.render(
				<div className='reactWidget flightSearch' data-testid='flights-search.widget.root'>
					<AppFlightsSearchWidget {...props} />
				</div>,
			);

			if (props?.callBack != null) {
				props.callBack(element);
			}
		});
	}
}
